// src/components/SettingsDropdown.tsx
// Settings dropdown with account options and sign out

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, LogOut, Bell, Palette } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface SettingsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsDropdown({ isOpen, onClose }: SettingsDropdownProps) {
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={onClose}
          />

          {/* Dropdown */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="fixed right-6 top-16 z-50 w-64 bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden"
          >
            <div className="p-2">
              <div className="flex items-center justify-between px-3 py-2 mb-1">
                <p className="text-xs font-medium text-purple-400 uppercase tracking-wide">
                  Settings
                </p>
                <button
                  onClick={onClose}
                  className="p-1 text-purple-400 hover:text-purple-600 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Future settings options */}
              <button
                className="w-full px-3 py-3 flex items-center gap-3 text-left text-purple-700 hover:bg-purple-50 rounded-xl transition-colors"
                disabled
                title="Coming soon"
              >
                <User className="w-4 h-4" />
                <span className="text-sm">Account</span>
                <span className="ml-auto text-xs text-purple-400">Soon</span>
              </button>

              <button
                className="w-full px-3 py-3 flex items-center gap-3 text-left text-purple-700 hover:bg-purple-50 rounded-xl transition-colors"
                disabled
                title="Coming soon"
              >
                <Bell className="w-4 h-4" />
                <span className="text-sm">Notifications</span>
                <span className="ml-auto text-xs text-purple-400">Soon</span>
              </button>

              <button
                className="w-full px-3 py-3 flex items-center gap-3 text-left text-purple-700 hover:bg-purple-50 rounded-xl transition-colors"
                disabled
                title="Coming soon"
              >
                <Palette className="w-4 h-4" />
                <span className="text-sm">Appearance</span>
                <span className="ml-auto text-xs text-purple-400">Soon</span>
              </button>

              <div className="my-2 border-t border-purple-100" />

              <button
                onClick={handleSignOut}
                className="w-full px-3 py-3 flex items-center gap-3 text-left text-red-600 hover:bg-red-50 rounded-xl transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default SettingsDropdown;
